import { Button, CheckmarkCircleFilledIcon } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { formatInTimeZone } from 'date-fns-tz'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Tag } from '@/components'
import { useMobile } from '@/hooks'
import { NewTicketDetails } from '@/types/Ticket'
import { getPriorityVariant } from '@/views/Ticket/Tickets/Tickets.data'

type Props = {
  ticket: NewTicketDetails
  toggle: () => void
}

export const CreateConfirmation = ({ ticket, toggle }: Props) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const createdAt = formatInTimeZone(ticket.createdAt, timezone, 'yyyy-MM-dd, h:mmaaa')
  const { isTablet } = useMobile()
  const navigate = useNavigate()

  const routeToTicket = () => {
    toggle()
    navigate(`/tickets/${ticket.id}`)
  }

  return (
    <ConfirmationStyled className="flex w-full flex-col flex-wrap px-6">
      <h2 className="mb-6 text-header-lg leading-120">Your ticket has been created!</h2>
      <p className=" mb-6 flex items-center rounded-2xl px-6 py-4 text-white bg-accent-dark">
        <CheckmarkCircleFilledIcon className="mr-4 h-4 text-black" />{' '}
        <span className="leading-none">Ticket Number: {ticket.id}</span>
      </p>
      <div className="ticket-line-item">
        <p>Subject</p>
        <p className="font-bold" style={{ alignItems: 'start' }}>
          {ticket.subject}
        </p>
      </div>
      {ticket.parentTicketId && (
        <div className="ticket-line-item">
          <p>Parent Ticket</p>
          <p
            className="cursor-pointer font-bold underline"
            onClick={() => {
              toggle()
              navigate(`/tickets/${ticket.parentTicketId}`)
            }}
            role="presentation"
          >
            #{ticket.parentTicketId}
          </p>
        </div>
      )}
      {ticket.reservationId && (
        <div className="ticket-line-item">
          <p>Reservation ID</p>
          <p className="font-bold">{ticket.reservationId}</p>
        </div>
      )}
      {ticket.homeId && (
        <div className="ticket-line-item ">
          <p>Home ID</p>
          <p className="font-bold"> {ticket.homeId}</p>
        </div>
      )}
      {ticket.contact && (
        <div className="ticket-line-item">
          <p>Member</p>
          <p className="font-bold">{ticket.contact.fullName}</p>
        </div>
      )}
      {ticket.priority && (
        <div className="ticket-line-item">
          <p>Priority</p>
          <span>
            <Tag className="text-p-sm" variant={getPriorityVariant(ticket.priority)}>
              {ticket.priority}
            </Tag>
          </span>
        </div>
      )}
      {ticket.marketSegment && (
        <div className="ticket-line-item">
          <p>Market Segment</p>
          <p className="h-fit-content self-center rounded-lg border px-3 py-2 font-bold text-p-sm leading-none border-primary">
            {ticket.marketSegment.name}
          </p>
        </div>
      )}
      {ticket.ticketType && (
        <div className="ticket-line-item">
          <p>Type</p>
          <p>{ticket.ticketType}</p>
        </div>
      )}
      {ticket.ticketReason && (
        <div className="ticket-line-item">
          <p>Reason</p>
          <p>{ticket.ticketReason}</p>
        </div>
      )}

      <div className="ticket-line-item">
        <p>Created At</p>
        <p>{createdAt}</p>
      </div>
      <div className="mb-4 flex w-full flex-col py-4">
        <p className="mb-2 font-bold leading-120 text-icon-tertiary">Description</p>
        <p>{ticket.description}</p>
      </div>
      <div className="flex flex-col-reverse justify-between md:flex-row">
        <Button
          className="w-full bg-action-secondary md:flex md:w-auto"
          onClick={toggle}
          size={isTablet ? 'medium' : 'small'}
          variant="secondary"
        >
          Close
        </Button>
        <Button
          className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
          onClick={routeToTicket}
          size={isTablet ? 'medium' : 'small'}
          type="submit"
        >
          View Ticket
        </Button>
      </div>
    </ConfirmationStyled>
  )
}

export const ConfirmationStyled = styled.div`
  .ticket-line-item {
    display: flex;
    height: 3.5rem;
    width: 100%;
    justify-content: space-between;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    overflow: hidden;
    border-bottom-width: 1px;
    border-color: #e7e6e4;
    margin-bottom: 0.25rem;
    p {
      &:first-of-type {
        line-height: 1.2;
        color: #787573;
        display: flex;
        align-items: center;
      }

      &:last-of-type {
        max-width: 50%;
        text-align: right;
        display: flex;
        align-items: center;
      }
    }
  }

  .font-bold {
    font-weight: 700;
  }
`
